import {PureComponent, createRef, RefObject, MouseEvent} from 'react';
import {isArray, isFunction, isObject} from 'underscore';
import {join} from '../../utils';
import {Renderable, RenderableFunction} from '../../core';
import {ExpandIcon} from '../../icons';
import {Selectors} from '../controls.selectors';
import DropdownContext, {DropdownContextType} from './dropdown-context';
import styles from './dropdown.module.less';
import {Popup} from './popup/popup';
import SelectableListContext from '../selectable-list/selectable-list-context';

class State {
	toggled: boolean = false;
}

export interface DropdownProps<T> {
	value: T[];
	setValue: (value: T[]) => void;
	validateOnBlur?: () => void;
	optionName?: keyof T;
	displayLabel?: (value: T[]) => string;
	multiselect?: boolean;
	placeholder?: string;
	tabIndex?: number;
	className?: string;
	placeholderClassName?: string;
	autoWidth?: boolean;
	disabled?: boolean;
	autoHeight?: boolean;
	children?: RenderableFunction<DropdownContextType<T>> | Renderable;
	'aria-invalid'?: boolean;
	dataCy?: string;
}

export class Dropdown<T> extends PureComponent<DropdownProps<T>, State> {
	public dropdownRef: RefObject<HTMLDivElement> = createRef();
	public override readonly state = new State();

	private readonly containerRef: RefObject<HTMLDivElement> = createRef();
	private blurTimer: number = 0;

	public override componentDidUpdate(prevProps: Readonly<DropdownProps<T>>) {
		if (prevProps.autoWidth !== this.props.autoWidth && !this.props.autoWidth) {
			if (this.containerRef.current) {
				this.containerRef.current.style.minWidth = '';
				this.containerRef.current.style.maxWidth = '';
			}
		}

		if (prevProps.disabled !== this.props.disabled && this.props.disabled) {
			this.setState({toggled: false});
		}
	}

	public override componentWillUnmount() {
		clearTimeout(this.blurTimer);
	}

	public override render() {
		return (
			<div
				className={join(styles.dropdownContainer, this.props.disabled && styles.disabled, this.props.className)}
				ref={this.containerRef}
			>
				{this.renderDropdown(this.props)}
			</div>
		);
	}

	public closeDropdown = () => {
		this.setState({toggled: false});
	};

	private renderLabel(value: T[]): JSX.Element {
		let text = '';

		if (!isArray(value)) {
			console.warn('[Dropdown]: Value in no array-like type. Dropdown works only with an array.');
		}

		if (!value.length) {
			text = this.props.placeholder || '';
		} else {
			if (this.props.displayLabel) {
				text = this.props.displayLabel(value);
			} else {
				text = value.filter(i => !!i).map(i => {
					if (this.props.optionName) {
						if (isObject(i)) {
							return i[this.props.optionName];
						} else {
							console.warn('[Dropdown]: Current value is not object, but \'optionName\' provided. Use \'optionName\' when value present as an object instead of primitive');
							return '';
						}
					} else {
						return i;
					}
				}).join(', ');
			}
		}

		return <span className={join(styles.label, !value.length && join(styles.emptyLabel, this.props.placeholderClassName))}>{text}</span>;
	}

	private renderDropdown(props: DropdownProps<T>) {
		return (
			<>
				<div
					ref={this.dropdownRef}
					data-cy={this.props.dataCy || Selectors.Dropdown}
					tabIndex={this.props.tabIndex || 0}
					className={join(styles.dropdownField, this.state.toggled && styles.activeDropdownField)}
					onKeyDown={(e) => {
						if (['enter', 'space'].includes(e.code.toLowerCase())) {
							this.setState({toggled: true});
						}
					}}
					aria-invalid={props['aria-invalid']}
					aria-disabled={props.disabled}
					onClick={(e) => this.handleOnClick(e, props)}
				>
					{this.renderLabel(props.value)}
					<ExpandIcon toggled={this.state.toggled}/>
				</div>
				<DropdownContext.Provider value={{
					dropdownRef: this.dropdownRef.current,
					currentValue: props.value,
					multiselect: Boolean(this.props.multiselect),
					toggled: this.state.toggled,
					onOptionClick: this.setValue,
				}}>
					<Popup
						toggled={this.state.toggled}
						onLooseFocus={() => {
							!!this.props.validateOnBlur && this.props.validateOnBlur();
							this.setState({toggled: false});
						}}
						autoHeight={this.props.autoHeight}
						autoWidth={this.props.autoWidth}
						handleAutoWidth={this.handleAutoWidth}
						targetRef={this.dropdownRef}
					>
					{this.renderChild()}
				</Popup>
			</DropdownContext.Provider>
			</>);
	}

	private renderChild(): Renderable {
		if (isFunction(this.props.children)) {
			return (
				<DropdownContext.Consumer>
					{(context: DropdownContextType<T> | null) => (this.props.children as Function)(context)}
				</DropdownContext.Consumer>
			);
		}
		if (this.props.multiselect) {
			return (
				<SelectableListContext.Provider value={{
					value: this.props.value,
					toggleValue: this.setValue,
					checkboxPlacement: 'left',
				}}>
					{this.props.children}
				</SelectableListContext.Provider>
			);
		}
		return this.props.children;
	}

	private handleAutoWidth = (width: number) => {
		if (!this.props.autoWidth) {
			return;
		}
		if (this.containerRef.current && width) {
			this.containerRef.current.style.minWidth = width + 'px';
			this.containerRef.current.style.maxWidth = width + 'px';
		}
	};

	private handleOnClick(e: MouseEvent<HTMLDivElement>, props: DropdownProps<T>) {
		if (!props.disabled) {
			if (!this.state.toggled) {
        e.currentTarget.focus();
			}
			this.setState({toggled: !this.state.toggled});
		}
	}

	private setValue = (value: T) => {
		const currentValue = this.props.value;
		const isSelected = currentValue.some(v => v === value);
		if (this.props.multiselect) {
			this.props.setValue(isSelected
				? currentValue.filter(v => v !== value)
				: [...currentValue, value]
			);
		} else {
			if (!isSelected) {
				this.props.setValue([value]);
			}
			this.setState({toggled: false}, () => this.dropdownRef.current?.focus());
		}
	};
}

export default Dropdown;
